import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const PANELS_STATIC = [
  {
    id: 'apartment',
    panelKey: 'panel1',
    image: 'https://public.readdy.ai/ai/img_res/edited_dce0a309bba79ca45c36975caac576bf_8609639d.jpg',
    overlayGradient: 'from-[#100700]/85 via-[#1e0c00]/48 to-[#0a0400]/20',
    icon: 'ri-building-4-line',
    tagColor: 'text-amber-200',
    iconBg: 'bg-amber-200/20 border-amber-200/40',
    iconText: 'text-amber-200',
    pillBg: 'bg-white/10 border border-white/20',
    ctaBg: 'bg-amber-900/80 hover:bg-amber-900',
    ctaText: 'text-amber-50',
    href: '#services',
    titleColor: 'text-white',
    subtitleColor: 'text-white/70',
    bodyColor: 'text-white/80',
  },
  {
    id: 'remodeling',
    panelKey: 'panel2',
    image: 'https://public.readdy.ai/ai/img_res/12c04f1cac7d0cf5d3ae62bbf861892c.jpg',
    overlayGradient: 'from-[#0d0500]/82 via-[#1e0c00]/42 to-[#0a0400]/18',
    icon: 'ri-hammer-line',
    tagColor: 'text-orange-600',
    iconBg: 'bg-orange-200/20 border-orange-200/40',
    iconText: 'text-orange-200',
    pillBg: 'bg-white/10 border border-white/20',
    ctaBg: 'bg-stone-800/80 hover:bg-stone-800',
    ctaText: 'text-orange-50',
    href: '#contact',
    titleColor: 'text-[#4A2E24]',
    subtitleColor: 'text-[#6B4438]',
    bodyColor: 'text-[#7A5248]',
  },
];

export default function Hero() {
  const { t } = useTranslation();
  const [hoveredPanel, setHoveredPanel] = useState<string | null>(null);

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative flex flex-col md:flex-row md:h-screen overflow-hidden">
      <div className="absolute top-0 left-0 right-0 z-20 pt-20 md:pt-28 pb-6 pointer-events-none">
        <div className="text-center">
          <p className="hidden md:block text-white/60 text-xs tracking-[0.3em] uppercase font-medium">
            {t('hero.brand')}
          </p>
        </div>
      </div>

      {PANELS_STATIC.map((panel, idx) => {
        const pk = panel.panelKey as 'panel1' | 'panel2';
        const feats = [
          t(`hero.${pk}.feat1`),
          t(`hero.${pk}.feat2`),
          t(`hero.${pk}.feat3`),
          t(`hero.${pk}.feat4`),
        ];
        return (
          <div
            key={panel.id}
            onMouseEnter={() => setHoveredPanel(panel.id)}
            onMouseLeave={() => setHoveredPanel(null)}
            className={`relative transition-all duration-700 ease-in-out overflow-hidden cursor-pointer h-[52vh] md:h-auto ${
              hoveredPanel === null
                ? 'md:flex-[1]'
                : hoveredPanel === panel.id
                ? 'md:flex-[1.35]'
                : 'md:flex-[0.65]'
            }`}
            style={{ minWidth: 0 }}
            onClick={() => handleScroll(panel.href)}
          >
            <img
              src={panel.image}
              alt={t(`hero.${pk}.title`)}
              className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 scale-100 hover:scale-105"
            />
            <div className={`absolute inset-0 bg-gradient-to-t ${panel.overlayGradient}`} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0f0600]/82 via-[#2a1000]/20 to-[#0a0400]/10" />

            {idx === 0 && (
              <div className="hidden md:block absolute right-0 top-0 bottom-0 w-px bg-white/20 z-10" />
            )}

            <div className="absolute inset-0 flex flex-col justify-end p-5 pb-7 md:p-10 md:pb-14">
              <div className="flex items-center gap-2 mb-2 md:mb-5">
                <div className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-full ${panel.iconBg}`}>
                  <i className={`${panel.icon} ${panel.iconText} text-xs md:text-sm`} />
                </div>
                <span className={`${panel.tagColor} text-xs tracking-widest uppercase font-semibold`}>
                  {t(`hero.${pk}.tag`)}
                </span>
              </div>

              <h2 className={`font-hero-title ${panel.titleColor} leading-tight mb-2 md:mb-3 whitespace-pre-line text-xl md:text-4xl xl:text-5xl`}>
                {t(`hero.${pk}.title`)}
              </h2>

              <p className={`${panel.subtitleColor} text-xs md:text-sm tracking-wide mb-3 md:mb-5 line-clamp-2 md:line-clamp-none`}>
                {t(`hero.${pk}.subtitle`)}
              </p>

              {/* desc - desktop only */}
              <div className={`hidden md:block overflow-hidden transition-all duration-500 ${hoveredPanel === panel.id ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'}`}>
                <p className={`${panel.bodyColor} text-sm leading-relaxed mb-4 md:mb-5`}>
                  {t(`hero.${pk}.desc`)}
                </p>
              </div>

              {/* feats - desktop only */}
              <div className={`hidden md:flex flex-wrap gap-2 mb-5 md:mb-6 overflow-hidden transition-all duration-500 ${hoveredPanel === panel.id ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'}`}>
                {feats.map((f) => (
                  <span
                    key={f}
                    className={`px-3 py-1 ${panel.pillBg} ${panel.bodyColor} rounded-full text-xs font-medium backdrop-blur-sm`}
                  >
                    {f}
                  </span>
                ))}
              </div>

              <div className="flex items-center">
                <button
                  onClick={(e) => { e.stopPropagation(); handleScroll(panel.href); }}
                  className={`flex items-center gap-2 px-4 py-2 md:px-6 md:py-3 ${panel.ctaBg} ${panel.ctaText} rounded-md text-xs md:text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer group`}
                >
                  <span>{t(`hero.${pk}.cta`)}</span>
                  <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </div>
        );
      })}

      <div className="absolute bottom-3 md:bottom-6 left-1/2 -translate-x-1/2 z-30 hidden md:flex flex-col items-center gap-2 pointer-events-none">
        <div className="flex items-center gap-1.5 px-3 md:px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
          <span className="w-1.5 h-1.5 rounded-full bg-[#67CBFF] animate-pulse" />
          <span className="text-white/70 text-xs tracking-widest uppercase">{t('hero.servingArea')}</span>
        </div>
      </div>
    </section>
  );
}
